import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { generateAiSummary, saveAiSummary } from '../../api/projectsApi';
import { useNavigate } from 'react-router-dom';

interface AiSummaryIframeProps {
  onCancel: () => void;
  onConfirm: (type: string) => void;
  comparisionId: string;
  userId: string;
  onEditSummary?: (summary: string) => void; // Add callback for editing summary
  onCloseAll?: () => void; // Add callback for closing entire iframe
}

const generationTypes = [
  { key: 'formal', label: 'Formal' },
  { key: 'friendly', label: 'Friendly' },
  { key: 'business', label: 'Business' },
];

const AiSummaryIframe: React.FC<AiSummaryIframeProps> = ({ onCancel, onConfirm, comparisionId, userId, onEditSummary, onCloseAll }) => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false); // Add state to control success view
  const [revisionId, setRevisionId] = useState<string | null>(null); // Store revisionId from backend
  const navigate = useNavigate();

  useEffect(() => {
    // Close Project Refresh Iframe if present
    if ((window as any).closeProjectRefreshIframe) {
      (window as any).closeProjectRefreshIframe();
    }
    // If a close function is provided via props, you can use it like below:
    // if (typeof onCloseProjectRefreshIframe === 'function') onCloseProjectRefreshIframe();
  }, []);

  const handleConfirm = async () => {
    if (!selectedType) return;
    if (!comparisionId || comparisionId.trim() === '') {
      setError('Comparison ID is missing or empty.');
      return;
    }
    setLoading(true);
    setError(null);
    setSummary(null);
    try {
      const data = await generateAiSummary(comparisionId, selectedType as 'formal' | 'friendly' | 'business', userId);
      if (data.success && data.summary) {
        setSummary(data.summary);
        setShowSuccess(true);
        if (data.comparisionId) {
          setRevisionId(data.comparisionId); // Save comparisionId if present
        }
      } else {
        setError(data.message || 'Failed to generate summary.');
      }
    } catch (err) {
      setError('Failed to generate summary.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSummary = () => {
    if (summary && onEditSummary) {
      onEditSummary(summary);
    }
  };

  // Save summary and go to pending post page
  const handleSaveAndEdit = async () => {
    if (!revisionId) return;
    setError(null);
    try {
      const data = await saveAiSummary(revisionId);
      if (data.success && data.pendingPostId) {
        navigate(`/pending-posts?pendingPostId=${data.pendingPostId}`);
      } else {
        setError(data.message || 'Failed to save summary.');
      }
    } catch (err) {
      setError('Failed to save summary.');
    }
  };

  // Save summary and just close iframe
  const handleSaveAndClose = async () => {
    if (!revisionId) return;
    if ((window as any).closeProjectRefreshIframe) {
      (window as any).closeProjectRefreshIframe();
    }
    setError(null);
    try {
      const data = await saveAiSummary(revisionId);
      if (data.success) {
        if (onCloseAll) onCloseAll();
        else onCancel();
      } else {
        setError(data.message || 'Failed to save summary.');
      }
    } catch (err) {
      setError('Failed to save summary.');
    } finally {
      // setLoading(false); // prevent loading overlay
    }
  };

  return (
    <div className="relative w-full h-full flex flex-col items-center">
      {/* Back button and summary card aligned in the same column */}
      {showSuccess && summary && (
        <div className="w-full max-w-2xl flex justify-start mt-0 pt-0 mb-0">
          <button
            className="px-4 py-1 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-semibold border border-gray-300 shadow transition-all duration-150 text-left"
            onClick={() => setShowSuccess(false)}
            type="button"
          >
            ← Back
          </button>
        </div>
      )}
      {/* Main content (loading, summary, or tone selection) */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-lg w-full flex flex-col items-center">
            <div className="mb-4 animate-spin">
              {/* Simple CSS spinner */}
              <svg className="h-10 w-10 text-custom-blue-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </div>
            <div className="text-lg font-semibold text-custom-blue-900 mb-2">Generating AI Summary...</div>
            <div className="text-sm text-gray-500">This may take a few seconds.</div>
          </div>
        </div>
      )}
      {showSuccess && summary ? (
        /* Success view - show only AI summary and action buttons */
        <div className="w-full max-w-2xl mt-2 p-6 bg-white border rounded shadow flex flex-col items-center">
          <div className="text-xl font-bold text-custom-blue-900 mb-4 text-center">AI Summary</div>
          <div className="w-full p-4 bg-gray-50 border rounded text-gray-900 mb-6">
            <div className="whitespace-pre-line text-sm leading-relaxed">{summary}</div>
          </div>
          <div className="flex w-full gap-4">
            <Button 
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              onClick={() => { handleSaveAndEdit(); }}
            >
              Edit This Summary
            </Button>
            <Button 
              className="flex-1 bg-custom-blue-600 hover:bg-custom-blue-700 text-white" 
              onClick={() => { handleSaveAndClose(); }}
            >
              Edit Later
            </Button>
          </div>
        </div>
      ) : (
        /* Initial view - show type selection and confirm buttons */
        <div className="w-full mt-6 p-4 bg-gray-50 border rounded shadow flex flex-col items-center">
          <div className="mb-4 text-base font-semibold text-center">Which type of summary do you want to generate?</div>
          <div className="flex flex-col gap-2 w-full">
            {generationTypes.map(type => (
              <Button
                key={type.key}
                className={`w-full transition-colors border ${selectedType === type.key ? 'bg-green-700 text-white border-green-800 font-bold' : 'bg-green-600 text-white border-transparent'} hover:bg-green-800 hover:text-white hover:border-green-900`}
                onClick={() => setSelectedType(type.key)}
                disabled={loading}
              >
                {type.label}
              </Button>
            ))}
          </div>
          <div className="flex w-full mt-6 gap-4">
            <Button className="flex-1 bg-custom-blue-600 hover:bg-custom-blue-700 text-white" onClick={onCancel} type="button" disabled={loading}>Generate Later</Button>
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              disabled={!selectedType || loading}
              onClick={handleConfirm}
            >
              {loading ? 'Generating...' : 'Confirm'}
            </Button>
          </div>
          {error && <div className="mt-4 text-red-600 text-sm">{error}</div>}
        </div>
      )}
    </div>
  );
};

export default AiSummaryIframe;
